import { BookOpen, Sparkles, ArrowRight, Check, Video } from "lucide-react";
import { useCMS, usePage, useStat, useSection } from "@/hooks/useCMS";
import { Eyebrow, StaggeredHeading, PillButton, Reveal, TiltCard } from "@/components/motifs";
import { CmsSection } from "@/components/CmsSection";
import { TemplateSections } from "@/components/TemplateSections";

const T = ["lg:-rotate-2", "lg:rotate-1", "lg:-rotate-1"];
const M = ["lg:mt-0", "lg:mt-10", "lg:mt-4"];

export default function BenniBooks() {
  const { data } = useCMS();
  const secSeries = useSection("bn_series");
  const page = usePage("benni");
  const books = data.books || [];
  const panels = data.panels || [];
  const s = data.settings || {};
  const hero = books[0] || {};
  const banner = books[2]?.cover || books[0]?.cover;

  return (
    <div data-testid="bennibooks-page" className="flex flex-col">
      {/* HERO */}
      <section className="mx-auto max-w-7xl px-4 sm:px-8 pt-14 pb-10 lg:pt-20">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <Eyebrow icon={BookOpen}>{page.eyebrow || "Benni & Friends"}</Eyebrow>
            <div className="mt-5">
              <StaggeredHeading animate lines={[
                { text: page.headline_1 || "Benni & Friends." },
                { text: page.headline_2 || "Read. Move.", emphasis: true, accent: "#DB1A5C" },
                { text: page.headline_3 || "Repeat." },
              ]} />
            </div>
            <p className="mt-6 max-w-md text-lg text-muted-foreground">{page.intro}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <PillButton href="#series" icon={ArrowRight} testid="benni-explore">{page.cta_primary_label || "Explore the series"}</PillButton>
              <PillButton to="/Events" variant="pink" icon={Sparkles} testid="benni-launch">{page.cta_secondary_label || "Oct 5 launch event"}</PillButton>
            </div>
            <div className="mt-9 flex gap-8">
              {[["3", "books"], ["4–8", "ages"], ["Free", "classroom kits"]].map(([v, l]) => (
                <div key={l}>
                  <p className="font-display font-black italic text-2xl text-[#2CACD4]">{v}</p>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{l}</p>
                </div>
              ))}
            </div>
          </div>

          <Reveal delay={0.1}>
            <div className="relative mx-auto max-w-sm">
              <div className="absolute -inset-8 dot-pattern -z-10 rounded-3xl" aria-hidden="true" />
              <div className="relative lg:-rotate-3">
                <img src={hero.cover} alt={hero.cover_alt || hero.title || "Benni & Friends book cover"} className="w-full rounded-2xl shadow-2xl" />
                <span className="absolute -top-4 -right-4 flex items-center gap-1.5 rounded-full bg-[#DB1A5C] px-4 py-2 text-xs font-black uppercase text-white shadow-lg">
                  <Sparkles size={14} /> {s.launch_badge || "Relaunching Oct 5"}
                </span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BANNER STRIP */}
      <CmsSection sk="bn_banner">
      <section className="mx-auto max-w-7xl px-4 sm:px-8 py-6">
        <div className="relative h-48 sm:h-60 overflow-hidden rounded-3xl shadow-lg">
          <img src={banner} alt="Benni & Friends illustration band" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2CACD4]/70 to-[#DB1A5C]/40" />
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="font-display font-black italic uppercase text-white text-2xl sm:text-4xl text-center drop-shadow-lg">Read a chapter. Try the move.</p>
          </div>
        </div>
      </section>

      </CmsSection>
      {/* SERIES */}
      <CmsSection sk="bn_series">
      <section id="series" className="mx-auto max-w-7xl px-4 sm:px-8 py-16 lg:py-24">
        <Reveal>
          <Eyebrow icon={BookOpen}>{secSeries.eyebrow || "The series"}</Eyebrow>
          <h2 className="mt-3 font-display font-black uppercase text-3xl sm:text-5xl leading-[0.95]">
            Three books. <span className="italic text-[#2CACD4]">Endless moves.</span>
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-8 md:grid-cols-3">
          {books.map((b, i) => {
            const isNew = (b.availability || "").toLowerCase().includes("new");
            return (
              <Reveal key={b.id || i} delay={i * 0.1}>
                <TiltCard accent={b.accent} tilt={T[i % 3]} mt={M[i % 3]} className="overflow-hidden h-full flex flex-col" testid={`book-${i}`}>
                  <div className="relative dot-pattern p-5">
                    <img src={b.cover} alt={b.cover_alt || b.title} className="w-full rounded-xl shadow-lg" />
                    <span className="absolute top-7 right-7 rounded-full px-3 py-1 text-xs font-black uppercase text-white shadow" style={{ background: isNew ? "#DB1A5C" : b.accent }}>{b.availability}</span>
                  </div>
                  <div className="p-6 flex flex-1 flex-col">
                    <p className="font-display font-black italic text-lg" style={{ color: b.accent }}>#{b.number}</p>
                    <h3 className="font-display font-black uppercase text-xl">{b.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground flex-1">{b.blurb}</p>
                    <div className="mt-4 flex items-center justify-between border-t border-border pt-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      <span>{b.ages}</span>
                      <span className="text-[#2CACD4]">{b.kit}</span>
                    </div>
                  </div>
                </TiltCard>
              </Reveal>
            );
          })}
        </div>
      </section>

      </CmsSection>
      {/* PANELS */}
      <CmsSection sk="bn_panels">
      <section className="bg-[#2CB6C2]/10 py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <Reveal>
            <Eyebrow icon={Sparkles}>Look inside</Eyebrow>
            <h2 className="mt-3 font-display font-black uppercase text-3xl sm:text-4xl">From the panels</h2>
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {panels.map((p, i) => (
              <Reveal key={p.id || i} delay={i * 0.1}>
                <figure className={`overflow-hidden rounded-3xl bg-card border border-border shadow-sm ${T[i % 3]}`}>
                  <img src={p.image} alt={p.image_alt || p.title} className="h-56 w-full object-cover" />
                  <figcaption className="p-5">
                    <p className="font-display font-black uppercase text-lg text-[#DB1A5C]">{p.title}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{p.caption}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      </CmsSection>
      {/* WHY IT WORKS */}
      <CmsSection sk="bn_why">
      <section className="mx-auto max-w-7xl px-4 sm:px-8 py-16 lg:py-24 grid gap-12 lg:grid-cols-2 lg:items-center">
        <Reveal>
          <h2 className="font-display font-black uppercase text-3xl sm:text-4xl leading-tight">
            Stories that get <span className="italic text-[#2CACD4]">small feet moving.</span>
          </h2>
          <ul className="mt-6 space-y-3">
            {["Free K–3 classroom kits with every book", "A try-it-yourself move page in each story", "Read-along videos to move at home"].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#CAE295] text-slate-900"><Check size={14} strokeWidth={3} /></span>
                <span className="text-foreground/80">{t}</span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="relative">
            <div className="absolute -inset-6 dot-pattern -z-10 rounded-3xl" aria-hidden="true" />
            <div className="rounded-3xl bg-card border border-border p-10 text-center shadow-xl">
              <p className="font-display font-black italic text-7xl text-[#DB1A5C]">{useStat("benni_lift") || "2×"}</p>
              <p className="mt-3 text-lg font-semibold">more likely to try the move</p>
              <p className="mt-1 text-sm text-muted-foreground">after reading with Benni & Friends</p>
            </div>
          </div>
        </Reveal>
      </section>

      </CmsSection>
      {/* LAUNCH CTA */}
      <CmsSection sk="bn_launch">
      <section className="mx-auto max-w-7xl px-4 sm:px-8 pb-20">
        <div className="rounded-[2rem] bg-[#DB1A5C] text-white p-10 sm:p-16 text-center shadow-2xl">
          <p className="eyebrow justify-center" style={{ color: "#fff" }}>{s.launch_date || "October 5, 2026"}</p>
          <h2 className="mt-4 font-display font-black uppercase text-3xl sm:text-5xl leading-[0.95]">Celebrate the relaunch at the Cycle Power Hour</h2>
          <p className="mx-auto mt-4 max-w-xl text-white/80">Every rider gets a first-printing Benni mini-comic. Bring the whole family.</p>
          <div className="mt-8 flex justify-center">
            <PillButton to="/Events" variant="dark" icon={ArrowRight} testid="benni-find-launch">Find the launch event</PillButton>
          </div>
        </div>
      </section>
      </CmsSection>
      <TemplateSections page="benni" />
    </div>
  );
}
